import React from "react";

import { AddRounded, CloseRounded, SaveRounded } from "@mui/icons-material";
import { Box, DialogContent } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { httpsCallable } from "firebase/functions";
import { DateTime } from "luxon";

import useFirebase from "../../../../../config/context/firebaseContext";
import useModal from "../../../../../config/context/modalContext";
import { GreyPallet } from "../../../../../config/styles";
import { Dev } from "../../../../../config/util/consoleUtil";

import ChipMain from "../../../../../components/InputMain/ChipMain";
import SelectMain from "../../../../../components/InputMain/SelectMain";
import SwitchMain from "../../../../../components/InputMain/SwitchMain";
import TextMain from "../../../../../components/InputMain/TextMain";
import MainCollapseBox from "../../../../../components/MainCollapseBox";
import { WideDialog } from "../../../../../components/MainModalSimple/styles";
import MainSolidButton from "../../../../../components/MainSolidButton";
import { ModalTransition } from "../../../../../components/ModalTransition";
import PageHeader from "../../../../../components/PageHeader";

import MainText from "../../../../../components/MainText";
import {
  BannerActionAvailableType,
  BannerItemType,
  BannerScreensAvailableType,
  BannerTypesAvailableType,
  ModalBaseType,
} from "../../../../../types/common";

type ModalWriteBannerProps = ModalBaseType & {
  banner?: BannerItemType;
};

export default function ModalWriteBanner({ onClose, banner }: ModalWriteBannerProps) {
  const { pushAlert } = useModal();
  const { functions } = useFirebase();

  const [open, setOpen] = React.useState(true);

  const [screenField, setScreenField] = React.useState<BannerScreensAvailableType>(
    banner?.screen || "bus"
  );
  const [typeField, setTypeField] = React.useState<BannerTypesAvailableType>(
    banner?.type || "default"
  );
  const [textField, setTextField] = React.useState(banner?.text || "");
  const [persistField, setPersistField] = React.useState(banner?.persist || false);
  const [dimissableField, setDimissableField] = React.useState(banner?.dimissable || false);

  const [actionContentField, setActionContentField] = React.useState(banner?.action?.content || "");
  const [actionTextField, setActionTextField] = React.useState(banner?.action?.text || "");
  const [actionTypeField, setActionTypeField] = React.useState<BannerActionAvailableType>(
    banner?.action?.type || "clipboard"
  );
  const [metaCacheField, setMetaCacheField] = React.useState(banner?.action?.meta?.cache || false);

  const [startField, setStartField] = React.useState<DateTime | null>(
    banner?.active?.start
      ? DateTime.fromMillis(banner?.active?.start)
      : DateTime.now().minus({ day: 2 })
  );
  const [endField, setEndField] = React.useState<DateTime | null>(
    banner?.active?.end
      ? DateTime.fromMillis(banner?.active?.end)
      : DateTime.now().minus({ day: 1 })
  );

  const [loading, setLoading] = React.useState(false);

  const handleWrite = async () => {
    try {
      setLoading(true);
      const writeConfigBanner = httpsCallable(functions, "writeRemoteConfigBanner");

      const payload = {
        operation: banner ? "update" : "create",
        banner: {
          screen: screenField,
          type: typeField,
          text: textField,
          persist: persistField,
          dimissable: dimissableField,
          active: { start: startField?.toMillis(), end: endField?.toMillis() },
          action: false,
        },
      } as any;

      if (banner) {
        payload.banner.id = banner.id;
        payload.id = banner.id;
      }

      if (actionContentField && actionTextField) {
        payload.banner.action = {
          content: actionContentField,
          text: actionTextField,
          type: actionTypeField,
          meta: { cache: metaCacheField },
        };
      }
      const response = await writeConfigBanner(payload);

      pushAlert({
        type: "success",
        message: (response?.data as any)?.message || "Configurações atualizadas",
      });

      handleClose(true);
    } catch (error) {
      console.log(...new Dev("handleWrite", error).Error());
      pushAlert({
        type: "error",
        message: `Erro ao salvar dados`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (reload: boolean = false) => {
    setOpen(false);
    setTimeout(() => {
      if (onClose) onClose(reload);
    }, 200);
  };

  return (
    <WideDialog
      fullWidth={true}
      open={open}
      onClose={() => handleClose()}
      TransitionComponent={ModalTransition}
    >
      <DialogContent>
        <PageHeader
          size="subtitle"
          title={banner ? "Editar" : "Criar Banner"}
          rightIcon={<CloseRounded />}
          onRightAction={() => handleClose()}
        />

        <Box>
          <Box>
            <Box mb={2}>
              <SelectMain
                name="Tela"
                value={screenField || ""}
                options={[
                  { name: "Ônibus", value: "bus" },
                  { name: "Cardápio", value: "menu" },
                  { name: "Spott", value: "spott" },
                  { name: "Avaliação", value: "rating" },
                ]}
                onChange={(value) => setScreenField(value as BannerScreensAvailableType)}
              />
            </Box>

            <Box mb={2}>
              <SelectMain
                name="Tipo"
                value={typeField || ""}
                options={[
                  { name: "Default", value: "default" },
                  { name: "Success", value: "success" },
                  { name: "Error", value: "error" },
                  { name: "Warning", value: "warning" },
                  { name: "Info", value: "info" },
                ]}
                onChange={(value) => setTypeField(value as BannerTypesAvailableType)}
              />
            </Box>

            <Box mt={2}>
              <TextMain
                lines={5}
                autoCapitalize="sentences"
                textColor={"regular"}
                name="Texto"
                onChange={setTextField}
                value={textField}
              />
            </Box>

            <Box mb={2}>
              <SwitchMain
                name="Persistente"
                checked={persistField}
                noBottom
                onChange={setPersistField}
              />
            </Box>

            <Box mb={2}>
              <SwitchMain
                name="Dimissível"
                checked={dimissableField}
                noBottom
                onChange={setDimissableField}
              />
            </Box>

            <Box display={"flex"} justifyContent={"center"} alignItems={"center"} gap={2} mt={4}>
              <DesktopDatePicker
                format="dd/LL/yy"
                orientation="portrait"
                label="Inicio"
                value={startField}
                onChange={(newValue: any) => setStartField(newValue)}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                  },
                }}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root.MuiOutlinedInput-root": {
                    borderRadius: "15px",
                    height: 60,
                    fontWeight: "bold",
                    fontSize: 16,
                    color: GreyPallet.Regular,
                    mb: 2,
                  },
                  "& .MuiFormLabel-root.MuiInputLabel-root": {
                    fontWeight: "bold",
                  },
                  "& .MuiFormLabel-root.MuiInputLabel-root.Mui-focused": {
                    color: GreyPallet.Regular,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "3px !important",
                    borderColor: `${GreyPallet.Light} !important`,
                    borderStyle: "solid",
                    "& .Mui-focused": {
                      borderColor: `${GreyPallet.Regular} !important`,
                    },
                  },
                }}
              />

              <DesktopDatePicker
                minDate={startField?.plus({ day: 1 })}
                format="dd/LL/yy"
                orientation="portrait"
                label="Fim"
                value={endField}
                onChange={(newValue: any) => setEndField(newValue)}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                  },
                }}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root.MuiOutlinedInput-root": {
                    borderRadius: "15px",
                    height: 60,
                    fontWeight: "bold",
                    fontSize: 16,
                    color: GreyPallet.Regular,
                    mb: 2,
                  },
                  "& .MuiFormLabel-root.MuiInputLabel-root": {
                    fontWeight: "bold",
                  },
                  "& .MuiFormLabel-root.MuiInputLabel-root.Mui-focused": {
                    color: GreyPallet.Regular,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "3px !important",
                    borderColor: `${GreyPallet.Light} !important`,
                    borderStyle: "solid",
                    "& .Mui-focused": {
                      borderColor: `${GreyPallet.Regular} !important`,
                    },
                  },
                }}
              />
            </Box>

            <MainCollapseBox title={"Action Button"} unmountOnExit>
              <Box>
                <TextMain
                  name="Conteúdo do Recurso"
                  value={actionContentField}
                  onChange={setActionContentField}
                  textColor={"regular"}
                />

                <TextMain
                  name="Texto do Botão"
                  value={actionTextField}
                  onChange={setActionTextField}
                  textColor={"regular"}
                />

                <Box display={"flex"} gap={1} flexWrap={"wrap"} mt={1}>
                  <ChipMain
                    options={[
                      { name: "Copiar", value: "clipboard" },
                      { name: "Link Externo", value: "external-link" },
                      { name: "Navegar Para", value: "navigation" },
                      { name: "Imagem", value: "image" },
                    ]}
                    onChange={(value) => setActionTypeField(value as BannerActionAvailableType)}
                    value={actionTypeField}
                  />
                </Box>

                <Box mt={2}>
                  <MainText color="ghost-regular" size="small">
                    Meta dados:
                  </MainText>
                  <Box mt={1}>
                    <SwitchMain
                      name="Cache"
                      checked={metaCacheField}
                      noBottom
                      onChange={setMetaCacheField}
                    />
                  </Box>
                </Box>
              </Box>
            </MainCollapseBox>
          </Box>

          <Box my={2}>
            <MainSolidButton
              loading={loading}
              text={banner ? "Salvar" : "Criar"}
              icon={banner ? <SaveRounded /> : <AddRounded />}
              onClick={() => handleWrite()}
            />
          </Box>
        </Box>
      </DialogContent>
    </WideDialog>
  );
}
